/**
 * FERP Portal - Task Group Detail Page (DRR-125 Sprint 4)
 *
 * Shows a single task group: metadata, ordered task list, and
 * per-actor progress tracking. Works for ANY task group in ANY
 * industry — competency areas, verification methods, and role
 * scoping are all rendered generically from backend data.
 *
 * Tabs:
 * - Definition: ordered task list (existing)
 * - Progress: per-actor completion heatmap (generic)
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Dumb Frontend: all data from backend taskGroup query
 * - No hardcoded field values — displays what backend returns
 * - Domain-agnostic: same renderer for healthcare, finance, legal, construction
 */

import { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTaskGroupDetail } from '@/hooks/useTaskGroups';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  Table,
  Paper,
  ThemeIcon,
  Alert,
  Button,
  Loader,
  Breadcrumbs,
  Anchor,
  Divider,
  Tabs,
  Progress,
  Tooltip,
  Accordion,
  SimpleGrid,
} from '@mantine/core';
import {
  IconListCheck,
  IconAlertTriangle,
  IconArrowLeft,
  IconShieldFilled,
  IconTag,
  IconChartBar,
  IconCheck,
  IconX,
  IconMinus,
  IconCertificate,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';
import { GqlTaskStatus, useListActorsQuery, useListTasksQuery } from '@/generated/graphql';

// Types from canonical hook
import type { TaskGroupDetail } from '@/hooks/useTaskGroups';
type ChildTask = TaskGroupDetail['children'][number];

interface AreaGroup {
  id: string;
  label: string;
  children: ChildTask[];
  lifeCriticalCount: number;
}

// ============================================================================
// HELPERS — all generic, no domain knowledge
// ============================================================================

/** Format a snake_case key to Title Case for display */
function formatLabel(key: string): string {
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Check if a child task's requiredRole list includes the given actor role */
function isChildRequiredForRole(child: ChildTask, actorRole: string): boolean {
  if (!child.requiredRole || child.requiredRole.length === 0) return true;
  return child.requiredRole.includes(actorRole);
}

/** Derive unique roles from all children's requiredRole arrays */
function deriveRoles(children: ChildTask[]): string[] {
  const roles = new Set<string>();
  for (const child of children) {
    if (child.requiredRole) {
      for (const r of child.requiredRole) roles.add(r);
    }
  }
  return Array.from(roles).sort();
}

/** Group children by competencyArea (returns null if no children have areas) */
function groupByArea(children: ChildTask[]): AreaGroup[] | null {
  const hasAreas = children.some((c) => c.competencyArea);
  if (!hasAreas) return null;

  const map = new Map<string, ChildTask[]>();
  for (const child of children) {
    const area = child.competencyArea ?? '_ungrouped';
    if (!map.has(area)) map.set(area, []);
    map.get(area)!.push(child);
  }

  return Array.from(map.entries()).map(([id, items]) => ({
    id,
    label: id === '_ungrouped' ? 'Ungrouped' : formatLabel(id),
    children: items,
    lifeCriticalCount: items.filter((c) => c.isLifeCritical).length,
  }));
}

// ============================================================================
// PROGRESS TAB — generic per-actor completion for any task group
// ============================================================================

function ProgressTab({ group }: { group: TaskGroupDetail }) {
  const tenantId = useTenantId() ?? '';
  const skip = !tenantId;

  const { data: actorsData, loading: actorsLoading } = useListActorsQuery({
    variables: { limit: 200 },
    skip,
  });
  const { data: tasksData, loading: tasksLoading } = useListTasksQuery({
    variables: { limit: 500 },
    skip,
  });

  const areas = useMemo(() => groupByArea(group.children), [group.children]);
  const allRoles = useMemo(() => deriveRoles(group.children), [group.children]);

  // Build completion map: actorId → Set<childTaskName>
  const completionMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const task of tasksData?.taskApiListTasks?.items ?? []) {
      if (
        task.status === GqlTaskStatus.COMPLETED &&
        (task as Record<string, unknown>).taskGroupRef === group.configId
      ) {
        const actorId = (task as Record<string, unknown>).assignedActorId as string ?? '';
        if (!map.has(actorId)) map.set(actorId, new Set());
        map.get(actorId)!.add(task.name ?? '');
      }
    }
    return map;
  }, [tasksData, group.configId]);

  const actors = useMemo(
    () => actorsData?.listActors?.items ?? [],
    [actorsData],
  );

  if (actorsLoading || tasksLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader />
        <Text c="dimmed">Loading progress data...</Text>
      </Group>
    );
  }

  if (actors.length === 0) {
    return (
      <Alert color="gray" title="No Actors">
        No actors found. Progress tracking requires actors assigned to this task group.
      </Alert>
    );
  }

  // Per-actor summary
  const actorSummaries = actors.map((actor) => {
    const actorRole = actor.actorType?.toLowerCase() ?? '';
    const completed = completionMap.get(actor.id) ?? new Set();
    const requiredChildren = group.children.filter((c) => isChildRequiredForRole(c, actorRole));
    const completedCount = requiredChildren.filter((c) => completed.has(c.name)).length;

    return {
      id: actor.id,
      name: actor.name ?? actor.id,
      role: actorRole,
      completed,
      requiredCount: requiredChildren.length,
      completedCount,
    };
  });

  return (
    <Stack gap="lg">
      {/* Summary cards */}
      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <Card withBorder p="sm">
          <Group>
            <ThemeIcon variant="light" color="blue" size="lg">
              <IconCertificate size={18} />
            </ThemeIcon>
            <div>
              <Text size="xs" c="dimmed">Fully Complete</Text>
              <Text fw={700}>
                {actorSummaries.filter((a) => a.completedCount === a.requiredCount && a.requiredCount > 0).length}
                {' / '}{actorSummaries.length}
              </Text>
            </div>
          </Group>
        </Card>
        <Card withBorder p="sm">
          <Group>
            <ThemeIcon variant="light" color="orange" size="lg">
              <IconChartBar size={18} />
            </ThemeIcon>
            <div>
              <Text size="xs" c="dimmed">In Progress</Text>
              <Text fw={700}>
                {actorSummaries.filter((a) => a.completedCount > 0 && a.completedCount < a.requiredCount).length}
              </Text>
            </div>
          </Group>
        </Card>
        <Card withBorder p="sm">
          <Group>
            <ThemeIcon variant="light" color="red" size="lg">
              <IconX size={18} />
            </ThemeIcon>
            <div>
              <Text size="xs" c="dimmed">Not Started</Text>
              <Text fw={700}>
                {actorSummaries.filter((a) => a.completedCount === 0).length}
              </Text>
            </div>
          </Group>
        </Card>
      </SimpleGrid>

      {/* Heatmap — if competency areas exist, use area columns; otherwise flat child columns */}
      <Paper withBorder p="md" style={{ overflowX: 'auto' }}>
        {areas ? (
          // Grouped by competency area
          <Table striped highlightOnHover withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ minWidth: 150 }}>Actor</Table.Th>
                <Table.Th style={{ minWidth: 70 }}>Role</Table.Th>
                <Table.Th style={{ minWidth: 90 }}>Progress</Table.Th>
                {areas.map((area) => (
                  <Table.Th key={area.id} style={{ textAlign: 'center', minWidth: 40 }}>
                    <Tooltip
                      label={`${area.label} (${area.children.length} modules${area.lifeCriticalCount > 0 ? `, ${area.lifeCriticalCount} life-critical` : ''})`}
                      multiline
                      w={200}
                    >
                      <Stack gap={2} align="center">
                        <Text size="xs" fw={600} style={{ writingMode: 'vertical-lr', transform: 'rotate(180deg)', maxHeight: 100 }}>
                          {area.label}
                        </Text>
                        {area.lifeCriticalCount > 0 && <Badge size="xs" color="red" variant="dot" />}
                      </Stack>
                    </Tooltip>
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {actorSummaries.map((actor) => (
                <Table.Tr key={actor.id}>
                  <Table.Td><Text size="sm" fw={500}>{actor.name}</Text></Table.Td>
                  <Table.Td><Badge size="xs" variant="light">{actor.role}</Badge></Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Progress
                        value={actor.requiredCount > 0 ? (actor.completedCount / actor.requiredCount) * 100 : 0}
                        size="sm"
                        color={actor.completedCount === actor.requiredCount ? 'green' : 'orange'}
                        style={{ flex: 1, minWidth: 50 }}
                      />
                      <Text size="xs" c="dimmed">{actor.completedCount}/{actor.requiredCount}</Text>
                    </Group>
                  </Table.Td>
                  {areas.map((area) => {
                    const required = area.children.filter((c) => isChildRequiredForRole(c, actor.role));
                    if (required.length === 0) {
                      return (
                        <Table.Td key={area.id} style={{ textAlign: 'center' }}>
                          <Tooltip label="Not required for this role">
                            <Badge variant="light" color="gray" size="sm"><IconMinus size={12} /></Badge>
                          </Tooltip>
                        </Table.Td>
                      );
                    }
                    const done = required.filter((c) => actor.completed.has(c.name)).length;
                    return (
                      <Table.Td key={area.id} style={{ textAlign: 'center' }}>
                        <Tooltip label={`${area.label}: ${done}/${required.length}`}>
                          <Badge
                            variant="filled"
                            color={done === required.length ? 'green' : 'red'}
                            size="sm"
                            style={{ minWidth: 32 }}
                          >
                            {done === required.length ? <IconCheck size={12} /> : `${done}/${required.length}`}
                          </Badge>
                        </Tooltip>
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ) : (
          // Flat — no competency areas, show per-child completion
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Actor</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>Progress</Table.Th>
                <Table.Th>Completed</Table.Th>
                <Table.Th>Remaining</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {actorSummaries.map((actor) => (
                <Table.Tr key={actor.id}>
                  <Table.Td><Text size="sm" fw={500}>{actor.name}</Text></Table.Td>
                  <Table.Td><Badge size="xs" variant="light">{actor.role}</Badge></Table.Td>
                  <Table.Td>
                    <Progress
                      value={actor.requiredCount > 0 ? (actor.completedCount / actor.requiredCount) * 100 : 0}
                      size="sm"
                      color={actor.completedCount === actor.requiredCount ? 'green' : 'orange'}
                      style={{ minWidth: 80 }}
                    />
                  </Table.Td>
                  <Table.Td><Text size="sm">{actor.completedCount}</Text></Table.Td>
                  <Table.Td><Text size="sm">{actor.requiredCount - actor.completedCount}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      {/* Per-area accordion with module detail (only when areas exist) */}
      {areas && (
        <>
          <Divider />
          <Title order={4}>Module Detail by Area</Title>
          <Accordion variant="separated">
            {areas.map((area) => (
              <Accordion.Item key={area.id} value={area.id}>
                <Accordion.Control>
                  <Group gap="sm">
                    <Text fw={500} size="sm">{area.label}</Text>
                    <Badge size="xs" variant="light">{area.children.length} modules</Badge>
                    {area.lifeCriticalCount > 0 && (
                      <Badge size="xs" color="red" variant="dot">
                        {area.lifeCriticalCount} life-critical
                      </Badge>
                    )}
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Table striped withTableBorder>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Module</Table.Th>
                        <Table.Th>Duration</Table.Th>
                        <Table.Th>Verification</Table.Th>
                        <Table.Th>Required Roles</Table.Th>
                        {actorSummaries.slice(0, 10).map((a) => (
                          <Table.Th key={a.id} style={{ textAlign: 'center' }}>
                            <Text size="xs">{a.name.split(' ')[0]}</Text>
                          </Table.Th>
                        ))}
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {area.children.map((child) => (
                        <Table.Tr key={child.id}>
                          <Table.Td>
                            <Group gap="xs">
                              <Text size="sm" fw={500}>{child.name}</Text>
                              {child.isLifeCritical && <Badge color="red" size="xs">LC</Badge>}
                            </Group>
                          </Table.Td>
                          <Table.Td><Text size="sm">{child.estimatedDurationMinutes ?? '—'} min</Text></Table.Td>
                          <Table.Td>
                            {child.verification ? (
                              <Badge size="xs" variant="light" color="blue">
                                {formatLabel(child.verification)}
                              </Badge>
                            ) : (
                              <Text size="xs" c="dimmed">—</Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs">
                              {child.requiredRole?.length ? child.requiredRole.join(', ') : 'All'}
                            </Text>
                          </Table.Td>
                          {actorSummaries.slice(0, 10).map((actor) => {
                            const req = isChildRequiredForRole(child, actor.role);
                            if (!req) {
                              return <Table.Td key={actor.id} style={{ textAlign: 'center' }}><IconMinus size={14} color="gray" /></Table.Td>;
                            }
                            return (
                              <Table.Td key={actor.id} style={{ textAlign: 'center' }}>
                                {actor.completed.has(child.name)
                                  ? <IconCheck size={14} color="green" />
                                  : <IconX size={14} color="red" />}
                              </Table.Td>
                            );
                          })}
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </>
      )}
    </Stack>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function TaskGroupDetailPage() {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();

  const { taskGroup: group, loading, error } = useTaskGroupDetail(groupId ?? '');

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Task Group">
          {error.message}
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      </Container>
    );
  }

  if (!group) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="orange" title="Not Found">
          Task group <strong>{groupId}</strong> was not found.
        </Alert>
        <Button mt="md" leftSection={<IconArrowLeft size={16} />} variant="light" onClick={() => navigate('/ferp-core/task-groups')}>
          Back to Task Groups
        </Button>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Breadcrumb */}
        <Breadcrumbs>
          <Anchor onClick={() => navigate('/ferp-core/task-groups')} style={{ cursor: 'pointer' }}>
            Task Groups
          </Anchor>
          <Text>{group.name}</Text>
        </Breadcrumbs>

        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <div>
            <Group gap="sm" mb={4}>
              <Title order={2}>{group.name}</Title>
              {group.isLifeCritical && (
                <Badge color="red" size="md" variant="filled" leftSection={<IconShieldFilled size={12} />}>
                  LIFE-CRITICAL
                </Badge>
              )}
            </Group>
            <Text c="dimmed" size="sm">{group.configId}</Text>
            {group.description && (
              <Text size="sm" mt="xs">{group.description}</Text>
            )}
          </div>
          <Button
            leftSection={<IconArrowLeft size={16} />}
            variant="light"
            onClick={() => navigate('/ferp-core/task-groups')}
          >
            Back
          </Button>
        </Group>

        {/* Metadata Cards */}
        <Group gap="md">
          <Card withBorder p="md" style={{ flex: 1 }}>
            <Text size="xs" c="dimmed" mb={4}>Category</Text>
            <Badge variant="light" color={group.color ?? 'blue'} size="lg">
              {group.category}
            </Badge>
          </Card>
          <Card withBorder p="md" style={{ flex: 1 }}>
            <Text size="xs" c="dimmed" mb={4}>Setting</Text>
            <Text size="sm" fw={500}>{group.setting}</Text>
          </Card>
          <Card withBorder p="md" style={{ flex: 1 }}>
            <Text size="xs" c="dimmed" mb={4}>Subgraph</Text>
            <Badge variant="outline" size="lg">{group.subgraph}</Badge>
          </Card>
          <Card withBorder p="md" style={{ flex: 1 }}>
            <Text size="xs" c="dimmed" mb={4}>Completion</Text>
            <Badge variant="light" color="gray" size="lg">
              {group.completionStrategy}
            </Badge>
          </Card>
          <Card withBorder p="md" style={{ flex: 1 }}>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm">
                <IconListCheck size={14} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Tasks</Text>
                <Text size="xl" fw={700}>{group.children.length}</Text>
              </div>
            </Group>
          </Card>
        </Group>

        {/* Tags */}
        {group.tags.length > 0 && (
          <Group gap="xs">
            <IconTag size={14} />
            {group.tags.map((tag) => (
              <Badge key={tag} variant="dot" size="sm" color="gray">{tag}</Badge>
            ))}
          </Group>
        )}

        <Divider />

        {/* Tabbed content: Definition + Progress */}
        <Tabs defaultValue="definition" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="definition" leftSection={<IconListCheck size={16} />}>
              Definition
            </Tabs.Tab>
            <Tabs.Tab value="progress" leftSection={<IconChartBar size={16} />}>
              Progress
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="definition" pt="md">
            <Paper withBorder>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>#</Table.Th>
                    <Table.Th>Task</Table.Th>
                    <Table.Th>Assignee Role</Table.Th>
                    <Table.Th>Duration (min)</Table.Th>
                    <Table.Th>Priority</Table.Th>
                    <Table.Th>Mandatory</Table.Th>
                    {group.children.some((c) => c.verification) && <Table.Th>Verification</Table.Th>}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {group.children.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={7}>
                        <Text ta="center" c="dimmed" py="md">No tasks defined</Text>
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    group.children.map((task) => (
                      <Table.Tr key={task.id}>
                        <Table.Td>
                          <Text size="sm" c="dimmed">{task.sequenceOrder}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Stack gap={0}>
                            <Group gap="xs">
                              <Text size="sm" fw={500}>{task.name}</Text>
                              {task.isLifeCritical && (
                                <Badge color="red" size="xs" variant="filled">LC</Badge>
                              )}
                              {task.competencyArea && (
                                <Badge size="xs" variant="light" color="violet">
                                  {formatLabel(task.competencyArea)}
                                </Badge>
                              )}
                            </Group>
                            {task.note && (
                              <Text size="xs" c="dimmed" lineClamp={1}>{task.note}</Text>
                            )}
                            <Text size="xs" c="dimmed" ff="monospace">{task.id}</Text>
                          </Stack>
                        </Table.Td>
                        <Table.Td>
                          {task.assigneeRole ? (
                            <Badge variant="light" size="sm" color="violet">{task.assigneeRole}</Badge>
                          ) : task.requiredRole?.length ? (
                            <Group gap={2}>
                              {task.requiredRole.map((r) => (
                                <Badge key={r} variant="light" size="xs" color="violet">{r}</Badge>
                              ))}
                            </Group>
                          ) : (
                            <Text size="sm" c="dimmed">—</Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{task.estimatedDurationMinutes ?? '—'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="outline" size="sm">{task.priority}</Badge>
                        </Table.Td>
                        <Table.Td>
                          {task.required ? (
                            <Badge color="red" size="sm" variant="light">Required</Badge>
                          ) : (
                            <Badge color="gray" size="sm" variant="light">Optional</Badge>
                          )}
                        </Table.Td>
                        {group.children.some((c) => c.verification) && (
                          <Table.Td>
                            {task.verification ? (
                              <Badge size="xs" variant="light" color="blue">
                                {formatLabel(task.verification)}
                              </Badge>
                            ) : (
                              <Text size="sm" c="dimmed">—</Text>
                            )}
                          </Table.Td>
                        )}
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
            </Paper>
          </Tabs.Panel>

          <Tabs.Panel value="progress" pt="md">
            <ProgressTab group={group} />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}

export default TaskGroupDetailPage;
